import { SubagentAccess } from 'js/src/agent/subagents/subagentAccess';
import { SingleSceneScope } from 'js/src/agent/subagents/SingleSceneScope';
import { TOOL_NAMES } from 'js/src/agent/toolNames';
import { SKILL_NAMES } from 'js/src/agent/skills/skillNames';
import { VideoData } from '@lumen5/luminary';
import { z } from 'zod';

import type { Conversation } from '../Conversation';
import type { MessageContent, ToolExecutionContext } from '../types';
import { Model, ReasoningEffort } from '../types';
import { captureSceneScreenshot, SCREENSHOT_MAX_EDGE } from '../rendering/sceneScreenshot';
import { ConversationTool } from '../tools/ConversationTool';
import { resolveSceneById } from '../tools/scene-addressing';
import {
    layeredGraphicPacket,
    layeredGraphicSystemPrompt,
    layeredGraphicTools,
} from './layeredGraphicChild';

import { checkpointVideoOperation } from 'js/src/data/video-data';
import type { RootState } from 'js/src/libs/RootState';
import { isVoiceDrivenSceneGroup } from 'js/src/luminary';
import { addHtmlAssetToSceneOperation } from 'js/src/luminary/html-asset-operations';
import { makeOperationsCallable } from 'js/src/luminary/luminary-hooks';
import { isSceneAnOverlay } from 'js/src/luminary/luminary-selectors';

export const designGraphicSchema = z.object({
    scene_id: z.string().describe('The scene_id from the video state to lay the graphic over.'),
    brief: z
        .string()
        .describe(
            'What the graphic shows and on which words, or — when the scene already has one — what ' +
                'to change about it.',
        ),
});
export type DesignGraphicArgs = z.infer<typeof designGraphicSchema>;

/** A design run plans, writes, lints and reviews; each of those can take a round or two to fix. */
const DESIGN_CHILD_MAX_ITERATIONS = 40;

/**
 * Lays a motion graphic over an existing scene, or revises the one already there, by handing the
 * authoring to a child built for it. A single layered graphic has no siblings to stay continuous
 * with, so it can be designed apart from the main conversation, on the model chosen for graphics.
 */
export class DesignGraphicTool extends ConversationTool<typeof designGraphicSchema> {
    protected override readonly schema = designGraphicSchema;
    protected override readonly toolName = TOOL_NAMES.designGraphic;
    // The designer renders and reviews the scene outside the capture queue parallel subagents share,
    // so like review_scene_images it is left to the agent that runs alone.
    public override readonly subagentAccess = SubagentAccess.UNAVAILABLE;
    public override readonly childModel = Model.DEEPSEEK_V4_1_FLASH_1017;
    public override readonly childReasoningEffort = ReasoningEffort.MEDIUM;
    protected override readonly toolDescription =
        'Lay a motion graphic over an existing scene, keeping everything the scene already shows ' +
        'beneath it, or change the graphic already there. A designer authors it from your brief. ' +
        `Load \`${SKILL_NAMES.layeredGraphicsBasics}\` before the first one.`;

    public override getToolDisplayName(): string {
        return 'Designing graphic';
    }

    public get statusIcon(): string {
        return 'code';
    }

    public async execute(args: DesignGraphicArgs, context: ToolExecutionContext) {
        const resolved = resolveSceneById(this.luminary, this.getState(), args.scene_id);
        if ('error' in resolved) {
            return `Error: ${resolved.error}`;
        }
        const { scene } = resolved;

        // A base scene spans whatever its group's overlays leave uncovered and is re-split as they
        // change, so a graphic on it would have no stable span to be timed against.
        const sceneGroup = scene.sceneGroup;
        if (
            sceneGroup &&
            isVoiceDrivenSceneGroup(sceneGroup) &&
            !isSceneAnOverlay(this.getState(), scene)
        ) {
            return (
                `Error: scene ${args.scene_id} is the base scene of a voice-driven scene group, ` +
                'whose span changes as overlays come and go. Give the graphic its own span first: ' +
                `${TOOL_NAMES.addVoiceoverScene} with this scene's id as the scene_design_id copies ` +
                'it as an overlay over the words you name — then design the graphic on that overlay.'
            );
        }

        const { addHtmlAssetToScene, checkpointVideo } = makeOperationsCallable(
            { addHtmlAssetToSceneOperation, checkpointVideoOperation },
            this.luminary,
            this.dispatch,
            this.getState as () => RootState,
        );
        const isNewGraphic = scene.getAssetsByType(VideoData.HTML_ASSET_TYPE).length === 0;
        if (!addHtmlAssetToScene(scene.id, '')) {
            return `Error: could not add a graphic to scene ${args.scene_id}.`;
        }
        this.startMotionGraphicsSceneBuilding(scene.id);

        let child: Conversation | null = null;
        child = this.createChildConversationForToolCall(context.toolCallId, {
            systemMessage: layeredGraphicSystemPrompt(),
            tools: layeredGraphicTools(this.luminary, this.dispatch, this.getState, () =>
                child ? child.getMessagesForAPI({ reportTruncation: false }) : [],
            ),
            maxIterations: DESIGN_CHILD_MAX_ITERATIONS,
            task: 'html-design',
            requiresVideoState: false,
            streaming: true,
            subagentScope: new SingleSceneScope(this.luminary, this.getState, scene.id),
            subagentDescription: 'Designing graphic',
        });

        const message: MessageContent = [
            {
                type: 'text',
                text: layeredGraphicPacket(this.luminary, this.getState(), scene, args.brief),
            },
        ];
        try {
            const still = await captureSceneScreenshot(
                this.luminary,
                scene,
                SCREENSHOT_MAX_EDGE.high,
            );
            message.push({ type: 'image_url', image_url: { url: still } });
        } catch {
            message.push({
                type: 'text',
                text: `(The still could not be rendered — look with ${TOOL_NAMES.reviewSceneImages} before planning.)`,
            });
        }
        await child.addUserMessage(message);

        const before = scene.getAssetsByType(VideoData.HTML_ASSET_TYPE)[0]?.html ?? '';
        const result = await child.executeWithLLM({ parentActionGroup: context.parentActionGroup });
        // Judged by what landed on the scene: the designer can be cut off after its last write and
        // review, and a finished graphic is worth keeping whether or not it got to say so.
        const [graphic] = scene.getAssetsByType(VideoData.HTML_ASSET_TYPE);
        const unchangedAfterFailure = !result.success && graphic?.html === before;
        if (!graphic?.html || unchangedAfterFailure) {
            // An empty graphic reads as a scene awaiting a prompt, which hides what the scene shows,
            // and only this tool can author it — so a failed first design takes it back off.
            if (isNewGraphic && graphic) {
                this.luminary.batchRun(() => {
                    graphic.getParent()?.removeChild(graphic);
                    graphic.destroy();
                });
                checkpointVideo({
                    systemTriggered: false,
                    parentActionGroup: context.parentActionGroup,
                });
            }
            return {
                result:
                    `Error: the graphic on scene ${args.scene_id} was not ${isNewGraphic ? 'designed' : 'changed'}` +
                    (result.message ? `: ${result.message}` : '.'),
                isSuccess: false,
            };
        }
        checkpointVideo({ systemTriggered: false, parentActionGroup: context.parentActionGroup });
        const summary = result.success
            ? result.message || 'Designed the graphic.'
            : `The graphic was designed, but the designer stopped before reporting on it (${result.message || 'no reason given'}). Review the scene before relying on it.`;
        return { result: `Success: ${summary}`, isSuccess: true };
    }
}
